import { useMemo } from 'react';
import { Form, FormActions, FormLayout, Modal } from 'ui';
import type { SlotStart } from 'features/calendar';
import {
  AttachmentsFormContent,
  type AttachmentsFormContentValues,
  DescriptionImageFormContent,
  type ExtendedDescriptionImageFormContentValues,
  type ExtendedReminderDetailFormContentValues,
  type ExtendedReminderDurationFormValues,
  ReminderDetailFormContent,
  ReminderDurationFormContent,
} from '../../components';
import { useUploadEventAttachments, useUploadEventCoverPhoto } from '../../hooks';
import {
  type ExtendedReminderInvitationsFormValues,
  ReminderInvitationsFormContent,
} from '../../invitations-form-content';
import { useCreateEvent } from '../../queries';
import { useCreateFormRequestData, useGetInitialValues } from './hooks';

export type CreateReminderFormValues = AttachmentsFormContentValues &
  ExtendedDescriptionImageFormContentValues &
  ExtendedReminderDetailFormContentValues &
  ExtendedReminderDurationFormValues &
  ExtendedReminderInvitationsFormValues;

type P = {
  onCancel: () => void;
  onSuccess: () => void;
  slotStart?: SlotStart;
};

const CreateReminderForm = ({ onCancel, onSuccess, slotStart }: P) => {
  const { isPending, mutate: createEvent } = useCreateEvent();
  const { isUploadingAttachments, uploadEventAttachments } = useUploadEventAttachments();
  const { isUploadingCoverPhoto, uploadEventCoverPhoto } = useUploadEventCoverPhoto();
  const createFormRequestData = useCreateFormRequestData();
  const initialValues = useGetInitialValues(slotStart);

  const submit = (formValues: CreateReminderFormValues) => {
    createEvent(createFormRequestData(formValues), {
      async onSuccess(eventData) {
        await uploadEventCoverPhoto({ eventId: eventData.id, heroImage: formValues.heroImage });
        await uploadEventAttachments({ attachments: formValues.attachments, eventId: eventData.id });
        onSuccess();
      },
    });
  };

  const isLoading = useMemo(
    () => isPending || isUploadingCoverPhoto || isUploadingAttachments,
    [isPending, isUploadingCoverPhoto, isUploadingAttachments],
  );

  return (
    <Form<CreateReminderFormValues> initialValues={initialValues} onFinish={submit}>
      <FormLayout>
        <ReminderDetailFormContent<CreateReminderFormValues> />
        <ReminderDurationFormContent<CreateReminderFormValues> />
        <ReminderInvitationsFormContent<CreateReminderFormValues> />
        <DescriptionImageFormContent<CreateReminderFormValues> />
        <AttachmentsFormContent<CreateReminderFormValues> />
      </FormLayout>
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            children: 'Cancel',
            onClick: onCancel,
          }}
          submit={{
            children: 'Set reminder',
            loading: isLoading,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default CreateReminderForm;
